// Notificaciones de UNIDAD (eventos del GPS, geocercas y viajes que genera
// V5-realtime con kind:'unit'). Viven aparte de los avisos de plataforma de la
// appsuite: campana propia en la barra del mapa, con historial y leído por
// usuario (/unit-notifications).
import api from "../../utils/api";
import { getJson } from "../../lib/http";

export interface UnitNotification {
  id: string;
  type: string | null;
  title: string;
  description: string;
  priority: string;
  device_id: string | null;
  vehicle_id: string | null;
  vehicle_name: string | null;
  lat: number | null;
  lon: number | null;
  /** Del momento del aviso; las notificaciones anteriores a V5-realtime#25 no los traen. */
  speed?: number | null;
  heading?: number | null; // grados, 0 = norte
  driver_name?: string | null;
  created_at: number | null; // epoch ms
  read: boolean;
}

export interface UnitInbox {
  items: UnitNotification[];
  unread: number;
}

const headers = (companyId: string) => ({ "x-company-id": companyId });

export const fetchUnitInbox = async (companyId: string): Promise<UnitInbox> => {
  const res = await getJson<{ data: { data: UnitInbox } }>("/unit-notifications/inbox", {}, undefined, { headers: headers(companyId) });
  return res.data.data;
};

export interface UnitHistory {
  items: UnitNotification[];
  truncated: boolean; // hubo más de 1000 en el rango
}

export const fetchUnitHistory = async (
  companyId: string,
  q: { from: string; to: string; vehicleId?: string | null; types?: string[] },
): Promise<UnitHistory> => {
  const res = await getJson<{ data: { data: UnitHistory } }>(
    "/unit-notifications",
    {
      from: q.from,
      to: q.to,
      ...(q.vehicleId ? { vehicle_id: q.vehicleId } : {}),
      ...(q.types?.length ? { type: q.types.join(",") } : {}),
    },
    undefined,
    { headers: headers(companyId) },
  );
  return res.data.data;
};

export const markUnitNotificationsRead = (companyId: string, body: { ids: string[] } | { all: true }) =>
  api.post("/unit-notifications/read", body, { headers: headers(companyId) });

export type Tone = "red" | "amber" | "green" | "blue" | "gray";

// Tipos que manda V5-realtime (notifications.service, geofenceMonitor,
// tripMonitor). Lo que no esté aquí sale con el ícono genérico.
const TYPES: Record<string, { icon: string; tone: Tone }> = {
  panic: { icon: "ri-alarm-warning-fill", tone: "red" },
  speed_alert: { icon: "ri-speed-up-line", tone: "amber" },
  engine_on: { icon: "ri-key-2-line", tone: "green" },
  engine_off: { icon: "ri-shut-down-line", tone: "gray" },
  engine_block_active: { icon: "ri-lock-line", tone: "red" },
  engine_block_released: { icon: "ri-lock-unlock-line", tone: "green" },
  device_disconnect: { icon: "ri-wifi-off-line", tone: "amber" },
  device_reconnect: { icon: "ri-wifi-line", tone: "green" },
  gps_signal_lost: { icon: "ri-map-pin-2-line", tone: "amber" },
  gps_signal_recovered: { icon: "ri-map-pin-2-fill", tone: "green" },
  GEOFENCE_ENTRY: { icon: "ri-login-box-line", tone: "blue" },
  GEOFENCE_EXIT: { icon: "ri-logout-box-r-line", tone: "blue" },
  // Viajes (tracking/helpers de V5-realtime).
  VIAJE_INICIADO: { icon: "ri-route-line", tone: "blue" },
  REGRESO_A_RUTA: { icon: "ri-route-line", tone: "green" },
  VIAJE_COMPLETADO: { icon: "ri-flag-2-line", tone: "green" },
  DEMORA_EN_INICIO: { icon: "ri-time-line", tone: "amber" },
  DESVIO_DE_RUTA: { icon: "ri-signpost-line", tone: "red" },
  SIGUE_FUERA_DE_RUTA: { icon: "ri-signpost-line", tone: "red" },
};

export const TYPE_LABEL: Record<string, string> = {
  panic: "Botón de pánico",
  speed_alert: "Exceso de velocidad",
  engine_on: "Motor encendido",
  engine_off: "Motor apagado",
  engine_block_active: "Paro de motor activado",
  engine_block_released: "Paro de motor liberado",
  device_disconnect: "Pérdida de conexión",
  device_reconnect: "Conexión restablecida",
  gps_signal_lost: "Sin señal GPS",
  gps_signal_recovered: "Señal GPS recuperada",
  GEOFENCE_ENTRY: "Entrada a geocerca",
  GEOFENCE_EXIT: "Salida de geocerca",
  VIAJE_INICIADO: "Viaje iniciado",
  REGRESO_A_RUTA: "Regresó a la ruta",
  VIAJE_COMPLETADO: "Viaje completado",
  DEMORA_EN_INICIO: "Demora en inicio",
  DESVIO_DE_RUTA: "Desvío de ruta",
  SIGUE_FUERA_DE_RUTA: "Sigue fuera de ruta",
};

export const typeLabel = (type: string | null) => (type && TYPE_LABEL[type]) || "Otra";

// El API manda "" cuando el doc no trae título: la campana pintaba un renglón
// vacío con sólo «hace 1 h». Sin título, el nombre del tipo.
export const notificationTitle = (n: Pick<UnitNotification, "title" | "type">) =>
  n.title?.trim() || typeLabel(n.type);

const GENERIC = { icon: "ri-notification-3-line", tone: "gray" as Tone };

export const typeMeta = (type: string | null) => (type && TYPES[type]) || GENERIC;

export const TONE_CLASS: Record<Tone, string> = {
  red: "bg-red-500/15 text-red-500",
  amber: "bg-amber-500/15 text-amber-500",
  green: "bg-green-500/15 text-green-500",
  blue: "bg-primary-500/15 text-primary-500",
  gray: "bg-gray-500/15 text-gray-400",
};

/** Barra lateral de la notificación desplegada: el color de su tipo la separa de las demás. */
export const TONE_ACCENT: Record<Tone, string> = {
  red: "before:bg-red-500",
  amber: "before:bg-amber-500",
  green: "before:bg-green-500",
  blue: "before:bg-primary-500",
  gray: "before:bg-gray-400",
};

/** «hace 3 min», «hace 2 h», o la fecha si pasó de un día. */
export const ago = (ms: number | null, now = Date.now()): string => {
  if (!ms) return "";
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 60) return "ahora";
  if (s < 3600) return `hace ${Math.floor(s / 60)} min`;
  if (s < 86400) return `hace ${Math.floor(s / 3600)} h`;
  return new Date(ms).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
};

const COMPASS = ["N", "NE", "E", "SE", "S", "SO", "O", "NO"];
/** Rumbo en grados → punto cardinal en español (0 = N, 90 = E). */
export const compass = (deg: number | null | undefined) =>
  deg == null || !Number.isFinite(deg) ? null : COMPASS[Math.round((((deg % 360) + 360) % 360) / 45) % 8];

/** «62 km/h · rumbo NE», o null si el aviso no trae velocidad. */
export const motionLabel = (n: Pick<UnitNotification, "speed" | "heading">) => {
  if (n.speed == null) return null;
  const dir = compass(n.heading);
  return `${Math.round(n.speed)} km/h${dir ? ` · rumbo ${dir}` : ""}`;
};
